import { ILogMechanism } from '../../Logging/ILogMechanism';
import { LogLevel } from '../../Logging/LogLevel';
import { ILogStore } from '../../Logging/ILogStore';
import { SignageLogItem } from '@reachmedianetwork/signage-abstractions';
import { BuiltInLogEventFilterRules, builtInLogSifter } from './BuiltInLogEventFilterRules';
import {
    buildObjectSifter,
    FilterRules,
    FilterRulesSchema,
    objectFilter,
    SifterRuleSet,
    siftObjects,
} from '@reachmedianetwork/object-filter';
import { Log } from '../../AuxillaryService/Log';
import axios from 'axios';
import { platform } from '../instance';
import { logRelay } from '../../MessagingClient/instance';
import AutoResetEvent from '../../Utility/AutoResetEvent';
import { asyncTimeout } from '../../Utility/AsyncTimeout';

type SafeSignageLogItem = Omit<SignageLogItem, 'data'> & { data: string };

/**
 * Attempts to acquire a filter rules object from the NamedValueCache entry
 * `relay_log_filter`. This method will log message(s) but return undefined if:
 *
 *  - platform does not define getNamedValue
 *  - the NamedValueCache entry `relay_log_filter` does not exist
 *  - `relay_log_filter` is not valid JSON
 *  - `relay_log_filter` does not decode to an object
 *  - `relay_log_filter` decodes to an object which fails validation
 */
async function getLogFilterFromNamedValueCache(): Promise<FilterRules | undefined> {
    try {
        const logFilter: string | undefined =
            (await platform().getNamedValue('relay_log_filter')) ?? undefined;

        if (null != logFilter) {
            const rules = FilterRulesSchema.parse(JSON.parse(logFilter));

            Log.Info('using custom log filter rules from NamedValueCache', null, __fileline);

            return rules;
        }
    } catch (e) {
        const message = e instanceof Error ? e.message : String(e);

        Log.Warning(
            `failed to acquire custom log filter stored in NamedValueCache: ${message}`,
            null,
            __fileline
        );
    }

    Log.Debug('NamedValueCache does not contain a valid set of log filter rules', null, __fileline);

    return undefined;
}

let lastLogFilterFromServer: FilterRules | undefined = undefined;
/**
 * Attempts to acquire a filter rules object from the Mercury Fx server. This
 * method will log message(s) but return undefined if:
 *
 *  - the server is unavailable
 *  - the server returns an un-expected response (no filter property)
 *  - the server's rule set fails validation
 *
 *  This method will cache a successful response from the server and return
 *  the cached response on future invocations if the a new filter rule set
 *  cannot be acquired from the server.
 */
async function getLogFilterFromServer(): Promise<FilterRules | undefined> {
    try {
        const response = await axios.get(`${process.env.BASE_URL_MERCURYFX}/api/logging/filter`);

        lastLogFilterFromServer = FilterRulesSchema.parse(response.data.filter);

        Log.Info(
            `successfully retrieved log filter rules from the Mercury Fx server (httpStatus: ${response.status})`,
            null,
            __fileline
        );
    } catch (e) {
        const message = e instanceof Error ? e.message : String(e);

        Log.Warning(
            `failed to acquire log filter from Mercury FX server: ${message}`,
            null,
            __fileline
        );

        if (null != lastLogFilterFromServer) {
            Log.Info('using cached log filter rules from Mercury FX server', null, __fileline);
        }
    }

    return lastLogFilterFromServer;
}

/**
 * a fake psuedo-random number generator that always returns 0. When passed to
 * an object filter, this will cause any sampling actions to always include the
 * object being assessed. This, in effect, changes any "sample" actions into
 * "include" actions.
 */
function AlwaysIncludePRNG() {
    return 0;
}

/**
 * Returns the built-in (hardcoded) filter rules. This method exists to log the
 * fact that the built-in rules are being used.
 */
function getBuiltInLogFilterRules(): FilterRules {
    Log.Info('using built-in log filter rules', null, __fileline);

    return BuiltInLogEventFilterRules;
}

export class REACHPlayerLogForwarderMechanism implements ILogMechanism, ILogStore {
    private readonly subcategoryExtractor = /^(?:.*)?\/(.*)\.(?:ts|js):[0-9]+$/;
    private readonly logsAvailableEvent = new AutoResetEvent();

    //use built-in log filter by default
    private effectiveFilterRules: SifterRuleSet = builtInLogSifter;

    constructor() {
        void this.logProcessingRoutine();

        REACH.Logger?.RegisterOnLogItemAvailableCallback(this.onLogItemAvailable);

        //and kick off an update right now
        void this.updateLogFilter();

        //pick a random time between now and 4 hours to update log filter again
        setTimeout(() => {
            void this.updateLogFilter();

            //update log filter from server every 4 hours hereafter
            setInterval(() => void this.updateLogFilter(), 14400000);
        }, Math.random() * 14400000);
    }

    /**
     * Attempts to update the log filter rules using the following sources in
     * order of precedence: NamedValueCache, Mercury FX Server, Built-in
     */
    private async updateLogFilter(): Promise<void> {
        this.effectiveFilterRules = buildObjectSifter(
            (await getLogFilterFromNamedValueCache()) ||
                (await getLogFilterFromServer()) ||
                getBuiltInLogFilterRules()
        );
    }

    private relayAllAvailableLogItems = async (): Promise<void> => {
        const theLogRelay = logRelay();
        if (!theLogRelay.isConnected || !theLogRelay.canRelayLogs) {
            return;
        }

        let lastLogCount: number;
        do {
            //do not filter the logs coming from getOldestLogs. we need to know
            //how many were fetched from C# and their IDs so we can delete them
            const logs = await this.getOldestLogs(10);
            lastLogCount = logs.length;

            if (lastLogCount < 1) {
                continue;
            }

            //use default Math.random PRNG to respect sampling rules now
            const filteredLogs = siftObjects(logs, this.effectiveFilterRules);

            if (filteredLogs.length > 0) {
                await theLogRelay.processLogs(filteredLogs);
            }

            await this.deleteLogs(logs);
        } while (lastLogCount === 10);
    };

    private logProcessingRoutine = async (): Promise<void> => {
        for (;;) {
            //wait for the event to be signalled
            if (await this.logsAvailableEvent.waitOne(60000)) {
                //wait 100 ms to see if more logs arrive
                await asyncTimeout(100);
            }

            //attempt to relay available logs regardless of whether we
            //were signalled; we might have missed a signal.
            try {
                await this.relayAllAvailableLogItems();
            } catch (e) {
                // probably should not add an error message to the log db
                // when we fail to transmit the log db...
                console.error('failed to send logs to Mercury FX', e);
            }
        }
    };

    private onLogItemAvailable = () => {
        //signal the event to let the processor Task know logs are here
        this.logsAvailableEvent.set();
    };

    private extractSubcategoryFromSource(source: string): string {
        this.subcategoryExtractor.lastIndex = 0;
        const matches = this.subcategoryExtractor.exec(source);

        if (matches && matches.length > 1) {
            return matches[1];
        }

        return 'Unknown';
    }

    public NamedEvent = (code: string, message: string, source: string, data: unknown): void => {
        REACH.Logger?.LogMessage(
            'info',
            message,
            code || 'UNKNOWN_EVENT',
            this.extractSubcategoryFromSource(source),
            JSON.stringify({
                trace: source,
                ...JSON.parse(JSON.stringify(data || {})),
            })
        );
    };

    public LogMessage(
        level: LogLevel,
        message: string,
        code: string | undefined | null,
        source: string,
        data?: unknown
    ): void {
        const subcategory = this.extractSubcategoryFromSource(source);
        const cleanData = JSON.stringify({
            trace: source,
            ...JSON.parse(JSON.stringify(data || {})),
        });

        //properties type, source, and category are added by underlying C#
        //logger and are potentially used by log filter rules. Set them
        //artificially here
        if (
            objectFilter(
                {
                    type: 'log',
                    source: 'player',
                    level,
                    message,
                    code: code || 'LOG_STATEMENT',
                    category: 'Presentation',
                    subcategory,
                    data: cleanData,
                },
                this.effectiveFilterRules,
                AlwaysIncludePRNG //don't sample when saving to C# layer
            )
        ) {
            REACH.Logger?.LogMessage(
                level,
                message,
                code || 'LOG_STATEMENT',
                subcategory,
                cleanData
            );
        }
    }

    public async deleteLogs(logs: SignageLogItem[]): Promise<void> {
        await this.deleteLogsById(logs.filter((log) => log.id).map((log) => log.id));
    }

    public deleteLogsById(logIds: string[]): Promise<void> {
        return new Promise((resolve, reject) => {
            REACH.Logger?.DeleteLogsById(logIds, resolve, reject);
        });
    }

    public getOldestLogs(limit: number): Promise<SignageLogItem[]> {
        return new Promise<SignageLogItem[]>((resolve, reject) => {
            REACH.Logger?.GetOldestLogs(
                limit,
                (logItems: SafeSignageLogItem[]) => {
                    resolve(
                        logItems.map((logItem) => ({
                            ...logItem,
                            data: JSON.parse(logItem.data),
                        }))
                    );
                },
                reject
            );
        });
    }
}
